import { Avatar, Card, cn } from "@heroui/react";
import React, { useState, useEffect } from "react";
import { JSX } from "react";
import { AnimatePresence, motion } from "motion/react";
import { User as UserType } from "@repo/types";
import { BadgeCheck } from "lucide-react";

export function User({ name, avatar_url, description }: { name?: string | JSX.Element, avatar_url?: string, description?: string | JSX.Element }) {
    return (
        <div className="flex gap-2 items-center">
            <UserAvatar avatar_url={avatar_url} />
            <div className="flex flex-col gap-1">
                {name}
                {description}
            </div>
        </div>
    )
}

export function UserAvatar({ avatar_url, size }: { avatar_url?: string, size?: "sm" | "md" | "lg" }) {
    return (
        <Avatar size={size || "md"}>
            <Avatar.Image src={avatar_url} />
            <Avatar.Fallback>
                <img src="/defaultAvatar.png" alt="avatar" className="w-full h-full" />
            </Avatar.Fallback>
        </Avatar>
    )
}

export const ComponentsUserWithHoverCard = ({ userInfo, children }: {
    userInfo?: UserType.Profile;
    children: React.ReactNode;
}) => {
    const [isHover, setIsHover] = useState<boolean>(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (isHover) {
                setMousePosition({ x: e.clientX, y: e.clientY });
            }
        };

        if (isHover) {
            document.addEventListener('mousemove', handleGlobalMouseMove);
        }

        return () => {
            document.removeEventListener('mousemove', handleGlobalMouseMove);
        };
    }, [isHover]);

    const handleMouseEnter = (e: React.MouseEvent) => {
        setIsHover(true);
        setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseLeave = () => {
        setIsHover(false);
    };

    const getCardPosition = () => {
        if (mousePosition.x === 0 && mousePosition.y === 0) {
            return { x: 0, y: 0 };
        }

        const cardWidth = 400;
        const cardHeight = 200;
        const offset = 15;

        let x = mousePosition.x + offset;
        let y = mousePosition.y + offset;

        if (x + cardWidth > window.innerWidth) {
            x = mousePosition.x - cardWidth - offset;
        }

        if (y + cardHeight > window.innerHeight) {
            y = mousePosition.y - cardHeight - offset;
        }

        x = Math.max(10, x);
        y = Math.max(10, y);

        return { x, y };
    };

    const cardPosition = getCardPosition();

    return (
        <>
            <div className="relative">
                <div onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}>
                    {children}
                </div>
                <AnimatePresence>
                    {isHover && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8, x: cardPosition.x, y: cardPosition.y }}
                            animate={{
                                opacity: 1,
                                scale: 1,
                                x: cardPosition.x,
                                y: cardPosition.y
                            }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{
                                opacity: { type: "spring", stiffness: 300, damping: 25, duration: 0.3 },
                                scale: { type: "spring", stiffness: 300, damping: 25, duration: 0.3 },
                                x: { type: "tween", duration: 0.1, ease: "easeOut" },
                                y: { type: "tween", duration: 0.1, ease: "easeOut" }
                            }}
                            className="fixed top-0 left-0 z-50 pointer-events-none"
                        >
                            <Card className="shadow-lg p-0 gap-0" style={{
                                width: '400px',
                                height: '240px',
                                maxHeight: '400px',
                                overflow: 'hidden',
                            }}>
                                {userInfo && (
                                    <Card.Content>
                                        <div
                                            style={{
                                                backgroundImage: userInfo?.banner_url ? `url(${userInfo.banner_url})` : `url('/images/banners/user_banner_default.png')`,
                                                backgroundColor: '#e5e7eb',
                                                height: '100px',
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center'
                                            }}
                                        >
                                            <Avatar style={{
                                                position: 'absolute',
                                                top: '100px',
                                                left: '64px',
                                                transform: 'translate(-50%, -50%)',
                                                height: '80px',
                                                width: '80px'
                                            }} >
                                                <Avatar.Image src={userInfo?.avatar_url} />
                                                <Avatar.Fallback>
                                                    <div style={{ width: "100%", height: "100%", backgroundColor: "gray" }} />
                                                </Avatar.Fallback>
                                            </Avatar>

                                        </div>
                                        <div className="flex flex-row gap-2 py-1 w-fit items-center" style={{ position: 'relative', left: '120px' }}>
                                            <div className="text-[#333333] text-lg font-medium">{userInfo?.nickname}</div>
                                            <div className="flex gap-1 items-center justify-start rounded-lg shrink-0 text-gray-500 h-6">
                                                <BadgeCheck className={userInfo?.real_name_verified ? 'text-green-500' : 'text-gray-500'} size={16} />
                                                <div className={cn("whitespace-nowrap text-xs", userInfo?.real_name_verified ? 'text-green-500' : 'text-gray-500')}>
                                                    {userInfo?.real_name_verified ? '已实名' : '未实名'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-2 p-4 px-6 text-sm">
                                            <div className="text-gray-500 line-clamp-1">{userInfo?.introduction || "TA很懒，暂无个人说明哦～"}</div>
                                            <div>
                                                最后在线：
                                                {userInfo.last_seem
                                                    ? (() => {
                                                        const date = new Date(userInfo.last_seem * 1000);
                                                        const year = date.getFullYear();
                                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                                        const day = String(date.getDate()).padStart(2, '0');
                                                        return `${year}年${month}月${day}日`;
                                                    })()
                                                    : <span className="text-gray-500">暂无数据</span>}
                                            </div>
                                        </div>
                                    </Card.Content>
                                )}
                            </Card>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
};
